import React, { useMemo } from "react";
import styles from "./header-buttons-icons.module.scss";
import { Tooltip } from "@whatslly/ui-components/Tooltip";
import classnames from "classnames";
import { Loader } from "@whatslly/ui-components/Loader";
import { SvgV2 } from "@whatslly/ui-components/v2/BaseElements/SvgV2";
import { EntityFilter } from "@whatslly/ui-components/EntityFilter";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { useDependencyInjector } from "@whatslly/commons/di";
import type { UserManagementUser } from "../../types/UserManagementUser";
import { UserManagementStore } from "../../stores/UserManagementStore";
import { FilterButton } from "../../../components/FilterButton";

export const HeaderButtonsIcons: React.FC<{ users: UserManagementUser[] }> = observer(({ users }) => {
  const { t } = useTranslation();
  const store = useDependencyInjector(UserManagementStore);
  const groupNames = useMemo(
    () => store.groupNames?.case({ fulfilled: (groupNames) => Object.fromEntries(groupNames.map((g) => [g._id, g.name])) }),
    [store.groupNames]
  );

  return (
    <div className={styles.headerButtonsIcons}>
      <Tooltip delay={300} value={t("user_management.download_csv")} placement="bottom">
        <div
          data-testid={`download-users-csv`}
          onClick={() => store.downloadCsvFile()}
          className={classnames(styles.headerButton, { [styles.disabled]: store.downloadCsvResult?.state === "pending" })}
        >
          {store.downloadCsvResult?.state === "pending" ? <Loader scale={0.25} /> : <SvgV2 width={20} height={20} iconName={"download"} />}
        </div>
      </Tooltip>
      <EntityFilter
        title={t("user_management.filters.title")}
        onSelectedFiltersChanged={(filters) => {
          store.filterGroups = filters.groupIds;
        }}
        initFilteredFields={{ groupIds: store.filterGroups }}
        filterSettings={
          groupNames
            ? [
                {
                  title: t("user_management.cols.groups"),
                  searchKey: "groupIds",
                  customObjectLabel: groupNames,
                },
              ]
            : []
        }
        BooleanFilters={[
          {
            text: t("user_management.filters.hide_users"),
            onChange: (newState: boolean) => {
              store.hideInactiveUsers = newState;
            },
            value: store.hideInactiveUsers,
            defaultValue: true,
          },
          {
            text: t("user_management.filters.restrict_to_direct_reports"),
            onChange: (newState: boolean) => {
              store.onlyShowRelatedToMe = newState;
            },
            value: store.onlyShowRelatedToMe,
            disabled: store.restrictToDirectReports,
            defaultValue: false,
          },
          {
            text: t("user_management.filters.online_only"),
            onChange: (newState: boolean) => {
              store.onlineOnly = newState;
            },
            value: store.onlineOnly,
            defaultValue: false,
          },
        ]}
        entities={users}
        EntityFilterButton={FilterButton}
        oppositeCheckedPosition={true}
      />
    </div>
  );
});
